import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, X, Search, LinkIcon, ExternalLink } from 'lucide-react';
import { apiRequest } from '../../config/api';
import CreateRecordModal from './CreateRecordModal';

/**
 * Generic RelationshipDrawer — renders related records for any ObjectRelationship.
 * Replaces the hardcoded Employment and Deals drawers.
 *
 * Props:
 *   relationship     – the relationship object from the API (with direction, display_name, etc.)
 *   recordId         – current record's ID
 *   objectDefinitionId – current record's object definition ID
 *   visible          – controls slide-in animation
 *   onClose          – close callback
 *   fetchRelatedRecords(relationshipId) – fetches related records
 *   createRecordLink(relId, sourceId, targetId, metadata) – for many_to_many
 *   updateRecordLink(linkId, metadata) – for many_to_many
 *   deleteRecordLink(linkId) – for many_to_many
 *   fetchRecord      – refetch the parent record (for many_to_one after creating a new record)
 */
export default function RelationshipDrawer({
  relationship,
  recordId,
  objectDefinitionId,
  visible,
  onClose,
  fetchRelatedRecords,
  createRecordLink,
  updateRecordLink,
  deleteRecordLink,
  fetchRecord,
}) {
  const [relatedData, setRelatedData] = useState({ items: [], total: 0, metadata_fields: [] });
  const [loadingRelated, setLoadingRelated] = useState(true);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [editMetadata, setEditMetadata] = useState({});
  const [showInactive, setShowInactive] = useState(false);

  // Search state for many_to_many linking
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // For many_to_one: "Add new record" modal
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [relatedFieldDefs, setRelatedFieldDefs] = useState([]);

  const rel = relationship;
  const isSource = rel?.direction === 'source';
  const isManyToMany = rel?.relationship_type === 'many_to_many';
  const relatedObjDef = rel?.related_object_definition;
  const relatedObjDefId = relatedObjDef?.id;

  // Load related records when drawer opens
  useEffect(() => {
    if (!rel) return;
    setLoadingRelated(true);
    setExpandedItemId(null);
    fetchRelatedRecords(rel.id).then((data) => {
      setRelatedData(data);
      setLoadingRelated(false);
    });
  }, [rel, fetchRelatedRecords]);

  // Fetch field definitions for the related object (for many_to_one "Add" modal)
  useEffect(() => {
    if (!relatedObjDefId || isManyToMany) return;
    const sourceObjDefId = rel.source_object_definition_id;
    apiRequest(`/crm/fields/?object_definition_id=${sourceObjDefId}`).then(async (res) => {
      if (res.ok) setRelatedFieldDefs(await res.json());
    }).catch(() => {});
  }, [relatedObjDefId, isManyToMany, rel]);

  // Search for records to link (many_to_many)
  const handleSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    if (!relatedObjDefId) return;
    setSearchLoading(true);
    try {
      const res = await apiRequest(`/crm/objects/${relatedObjDefId}/records-search?q=${encodeURIComponent(q)}&limit=10`);
      if (res.ok) {
        const results = await res.json();
        // Filter out already-linked records
        const linkedIds = new Set(relatedData.items.map((item) => item.record.id));
        setSearchResults(results.filter((r) => !linkedIds.has(r.id)));
      }
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setSearchLoading(false);
    }
  }, [relatedObjDefId, relatedData.items]);

  // Link a record (many_to_many)
  const handleLink = async (targetId) => {
    if (!rel) return;
    const sourceId = isSource ? recordId : targetId;
    const targetIdFinal = isSource ? targetId : recordId;
    const success = await createRecordLink(rel.id, sourceId, targetIdFinal, {});
    if (success) {
      setSearchQuery('');
      setSearchResults([]);
      // Refetch related records
      const data = await fetchRelatedRecords(rel.id);
      setRelatedData(data);
    }
  };

  // Delete/unlink
  const handleDelete = async (itemId) => {
    if (isManyToMany) {
      const success = await deleteRecordLink(itemId);
      if (success) {
        setRelatedData((prev) => ({
          ...prev,
          items: prev.items.filter((i) => i.id !== itemId),
          total: prev.total - 1,
        }));
        if (expandedItemId === itemId) setExpandedItemId(null);
      }
    } else {
      // many_to_one: delete the source record
      if (!window.confirm('Are you sure you want to delete this record?')) return;
      try {
        const item = relatedData.items.find((i) => i.id === itemId);
        if (item) {
          await apiRequest(`/crm/objects/${rel.source_object_definition_id}/records/${itemId}`, { method: 'DELETE' });
          setRelatedData((prev) => ({
            ...prev,
            items: prev.items.filter((i) => i.id !== itemId),
            total: prev.total - 1,
          }));
          if (expandedItemId === itemId) setExpandedItemId(null);
          fetchRecord();
        }
      } catch (e) {
        console.error('Delete error:', e);
      }
    }
  };

  // Save metadata edit (many_to_many)
  const handleSaveMetadata = async () => {
    if (!expandedItemId) return;
    const success = await updateRecordLink(expandedItemId, editMetadata);
    if (success) {
      const data = await fetchRelatedRecords(rel.id);
      setRelatedData(data);
      setExpandedItemId(null);
    }
  };

  // Open item detail
  const openItemDetail = (item) => {
    setEditMetadata(item.link_metadata || {});
    setExpandedItemId(item.id);
  };

  if (!rel) return null;

  const metadataFields = relatedData.metadata_fields || rel.metadata_fields || [];
  // For many_to_many with is_active metadata, split active/inactive
  const hasActiveField = metadataFields.some((f) => f.slug === 'is_active');
  const activeItems = hasActiveField
    ? relatedData.items.filter((i) => i.link_metadata?.is_active !== false)
    : relatedData.items;
  const inactiveItems = hasActiveField
    ? relatedData.items.filter((i) => i.link_metadata?.is_active === false)
    : [];

  const expandedItem = expandedItemId ? relatedData.items.find((i) => i.id === expandedItemId) : null;

  // For many_to_one: determine which field to pre-fill and which to hide
  const sourceFieldSlug = rel.source_field_definition_id
    ? relatedFieldDefs.find((fd) => fd.id === rel.source_field_definition_id)?.slug
    : null;

  const getDrawerTitle = () => {
    if (expandedItemId) {
      return isManyToMany ? 'Edit Link' : 'Details';
    }
    return rel.display_name;
  };

  // Navigate to related record
  const getRecordHref = (item) => {
    const slug = relatedObjDef?.slug_plural || relatedObjDef?.slug;
    if (slug && item.record?.id) return `/data/${slug}/${item.record.id}`;
    return null;
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      <div className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-white border-l border-gray-100 shadow-xl rounded-l-2xl flex flex-col transition-transform ${visible ? 'translate-x-0 duration-300 ease-out' : 'translate-x-full duration-200 ease-in'}`}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {expandedItemId && (
              <button
                onClick={() => setExpandedItemId(null)}
                className="p-1 -ml-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h3 className="text-base font-semibold text-gray-900 tracking-tight">
              {getDrawerTitle()}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — sliding panels */}
        <div className="overflow-hidden flex-1">
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ width: '200%', transform: expandedItemId ? 'translateX(-50%)' : 'translateX(0)' }}
          >
            {/* Panel 1: List */}
            <div className="w-1/2 h-full overflow-y-auto p-6">
              {loadingRelated ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Search / Link section for many_to_many */}
                  {isManyToMany && (
                    <div className="space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          placeholder={`Link ${relatedObjDef?.name || 'record'}...`}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
                        />
                      </div>
                      {searchResults.length > 0 && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          {searchResults.map((r) => (
                            <button
                              key={r.id}
                              onClick={() => handleLink(r.id)}
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-brand-50 border-b border-gray-100 last:border-0 transition-colors flex items-center gap-2.5"
                            >
                              <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium flex items-center justify-center border border-gray-200/60 flex-shrink-0">
                                {(r.label || '?')[0]?.toUpperCase()}
                              </div>
                              <span className="truncate">{r.label}</span>
                              <LinkIcon className="w-3.5 h-3.5 text-gray-300 ml-auto flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}
                      {searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
                        <p className="text-xs text-gray-400 text-center py-2">No results found</p>
                      )}
                    </div>
                  )}

                  {/* Add button for many_to_one */}
                  {!isManyToMany && (
                    <>
                      <button
                        onClick={() => setShowAddRecord(true)}
                        className="flex items-center text-sm text-brand-600 hover:text-brand-700 font-medium transition-colors"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add {relatedObjDef?.name || 'Record'}
                      </button>
                      <CreateRecordModal
                        isOpen={showAddRecord}
                        onClose={() => setShowAddRecord(false)}
                        onCreated={async () => {
                          fetchRecord();
                          const data = await fetchRelatedRecords(rel.id);
                          setRelatedData(data);
                        }}
                        objectDefinitionId={rel.source_object_definition_id}
                        fieldDefinitions={relatedFieldDefs}
                        defaultValues={sourceFieldSlug ? { [sourceFieldSlug]: recordId } : {}}
                        hiddenFields={sourceFieldSlug ? [sourceFieldSlug] : []}
                      />
                    </>
                  )}

                  {/* Items list */}
                  {activeItems.length === 0 && !loadingRelated ? (
                    <p className="text-sm text-gray-400 text-center py-8">No records linked yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {activeItems.map((item) => (
                        <RelatedRecordRow
                          key={item.id}
                          item={item}
                          metadataFields={metadataFields}
                          isManyToMany={isManyToMany}
                          href={getRecordHref(item)}
                          onClick={() => openItemDetail(item)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Inactive / Former items */}
                  {inactiveItems.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowInactive(!showInactive)}
                        className="flex items-center text-sm text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showInactive ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                        Former ({inactiveItems.length})
                      </button>
                      {showInactive && (
                        <div className="space-y-2 mt-2">
                          {inactiveItems.map((item) => (
                            <RelatedRecordRow
                              key={item.id}
                              item={item}
                              metadataFields={metadataFields}
                              isManyToMany={isManyToMany}
                              href={getRecordHref(item)}
                              onClick={() => openItemDetail(item)}
                              inactive
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Panel 2: Detail / Edit */}
            <div className="w-1/2 h-full overflow-y-auto p-6">
              {expandedItem && (
                <div className="space-y-5">
                  {/* Record info header */}
                  <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                    <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center border border-gray-200/60">
                      {(expandedItem.display_name || '?')[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{expandedItem.display_name}</p>
                      {getRecordHref(expandedItem) && (
                        <Link
                          to={getRecordHref(expandedItem)}
                          className="text-xs text-brand-500 hover:text-brand-600 flex items-center gap-1 mt-0.5 transition-colors"
                        >
                          View record <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Metadata fields (many_to_many) */}
                  {isManyToMany && metadataFields.length > 0 && (
                    <div className="space-y-4">
                      {metadataFields.map((mf) => (
                        <MetadataFieldEditor
                          key={mf.id || mf.slug}
                          field={mf}
                          value={editMetadata[mf.slug]}
                          onChange={(val) => setEditMetadata((prev) => ({ ...prev, [mf.slug]: val }))}
                        />
                      ))}

                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          onClick={() => setExpandedItemId(null)}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveMetadata}
                          className="px-4 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Record data fields (many_to_one) */}
                  {!isManyToMany && expandedItem.record?.data && (
                    <div className="space-y-3">
                      {Object.entries(expandedItem.record.data).map(([key, value]) => {
                        if (key === sourceFieldSlug || value == null || value === '') return null;
                        return (
                          <div key={key} className="flex items-center justify-between py-1.5">
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{key.replace(/_/g, ' ')}</span>
                            <span className="text-sm text-gray-700 truncate ml-4 text-right">{String(value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Delete / Unlink */}
                  <div className="pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleDelete(expandedItemId)}
                      className="flex items-center text-sm text-red-500 hover:text-red-600 font-medium transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      {isManyToMany ? 'Remove link' : 'Delete record'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


/** A single row in the related records list */
function RelatedRecordRow({ item, metadataFields, isManyToMany, href, onClick, inactive }) {
  // Pick a subtitle from metadata (first text field)
  const subtitle = isManyToMany
    ? metadataFields
        .filter((f) => f.field_type === 'text')
        .map((f) => item.link_metadata?.[f.slug])
        .filter(Boolean)
        .join(' · ')
    : null;

  return (
    <div
      className={`flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors ${inactive ? 'opacity-50 hover:opacity-70' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200/60">
          <span className="text-[10px] font-semibold text-gray-500">
            {(item.display_name || '?')[0]?.toUpperCase()}
          </span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{item.display_name}</p>
          {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
    </div>
  );
}


/** Renders an edit control for a single metadata field */
function MetadataFieldEditor({ field, value, onChange }) {
  const baseClasses = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500";

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{field.name}</label>
      {field.field_type === 'boolean' ? (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onChange(true)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
              value === true
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => onChange(false)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
              value === false
                ? 'bg-gray-100 text-gray-700 border-gray-300'
                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            No
          </button>
        </div>
      ) : field.field_type === 'date' ? (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={baseClasses}
        />
      ) : field.field_type === 'number' ? (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          placeholder={field.name}
          className={baseClasses}
        />
      ) : field.field_type === 'select' ? (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={baseClasses}
        >
          <option value="">Select...</option>
          {(field.options?.choices || []).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={field.name}
          className={baseClasses}
        />
      )}
    </div>
  );
}
